import React, { useContext, useState, useMemo } from "react";
import { CartContext } from "../../Contexts/CartContext";
import axios from "axios";
import "../../App.css";

const Checkout = () => {
  const {
    cartItems,
    updateQuantity,
    totalAmount: subtotal,
  } = useContext(CartContext);
  const [step, setStep] = useState(1);
  const [address, setAddress] = useState({
    fullName: "",
    phoneNumber: "",
    email: "",
    houseNumber: "",
    area: "",
    city: "",
    state: "",
    pincode: "",
  });
  //lazy-load code
  const loadRazorpayScript = () => {
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true; // Load asynchronously
      script.onload = () => resolve(true);
      script.onerror = () => reject(false);
      document.body.appendChild(script);
    });
  };
  //lazy-load code ends
  const [errors, setErrors] = useState({});
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  // Constants
  const GST_RATE = 0.18; // 18%

  // Calculate GST per item and total GST using useMemo for optimization
  const cartItemsWithGST = useMemo(() => {
    return cartItems.map((item) => {
      const gstAmount = item.price * item.quantity * GST_RATE;
      const totalPriceWithGST = item.price * item.quantity + gstAmount;
      return {
        ...item,
        gstAmount: parseFloat(gstAmount.toFixed(2)),
        totalPriceWithGST: parseFloat(totalPriceWithGST.toFixed(2)),
      };
    });
  }, [cartItems]);

  const totalGST = useMemo(() => {
    return parseFloat(
      cartItemsWithGST.reduce((acc, item) => acc + item.gstAmount, 0).toFixed(2)
    );
  }, [cartItemsWithGST]);

  const totalAmountWithGST = useMemo(() => {
    return parseFloat((subtotal + totalGST).toFixed(2));
  }, [subtotal, totalGST]);

  // Function to validate each field
  const validateField = (name, value) => {
    let error = "";
    switch (name) {
      case "fullName":
        if (!value.match(/^[a-zA-Z\s]+$/)) {
          error = "Full Name can only contain letters.";
        }
        break;
      case "phoneNumber":
        if (!value.match(/^\d{10}$/)) {
          error = "Contact Number must be exactly 10 digits.";
        }
        break;
      case "email":
        if (!value.match(/^[\w-\.]+@([\w-]+\.)+[\w-]{2,4}$/)) {
          error = "Please enter a valid email address.";
        }
        break;
      case "houseNumber":
        if (!value.trim()) {
          error = "House No./Building Name is required.";
        }
        break;
      case "area":
        if (!value.trim()) {
          error = "Road Name/Colony is required.";
        }
        break;
      case "city":
        if (!value.match(/^[a-zA-Z\s]+$/)) {
          error = "City can only contain letters.";
        }
        break;
      case "state":
        if (!value.match(/^[a-zA-Z\s]+$/)) {
          error = "State can only contain letters.";
        }
        break;
      case "pincode":
        if (!value.match(/^\d{6}$/)) {
          error = "Pincode must be exactly 6 digits.";
        }
        break;
      default:
        break;
    }
    return error;
  };

  // Handle moving to the next step
  const handleNextStep = (e) => {
    e.preventDefault();

    // Validate all fields before proceeding to the next step
    const fieldErrors = {};
    Object.keys(address).forEach((field) => {
      const error = validateField(field, address[field]);
      if (error) fieldErrors[field] = error;
    });

    setErrors(fieldErrors);

    if (Object.keys(fieldErrors).length === 0) {
      setStep((prevStep) => (prevStep < 3 ? prevStep + 1 : prevStep)); // Increment step up to 3
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAddress({ ...address, [name]: value });

    if (value) {
      setErrors({ ...errors, [name]: "" });
    }
  };

  // Razorpay payment integration
  const handleRazorpayPayment = async () => {
    const res = await loadRazorpayScript();
    if (!res) {
      alert("Failed to load Razorpay. Please check your internet connection.");
      return;
    }
    const options = {
      key: "rzp_live_40PTwkE1n9MCd1", // Replace with your Razorpay key
      amount: totalAmountWithGST * 100, // Amount in paise
      currency: "INR",
      name: "ROYAL GREEN HOUSE CONSTRUCTION(I) PVT. LTD.",
      description: "Order Payment",
      handler: async function (response) {
        alert(
          `Payment Successful! Payment ID: ${response.razorpay_payment_id}`
        );
        setPaymentSuccess(true);

        // Prepare order data
        const orderData = {
          fullName: address.fullName,
          phoneNumber: address.phoneNumber,
          areaColony: address.area,
          houseNumber: address.houseNumber,
          city: address.city,
          state: address.state,
          pincode: address.pincode,
          subtotal: subtotal,
          totalGST: totalGST,
          totalPrice: totalAmountWithGST,
          paymentMethod: "Razorpay",
          paymentStatus: "Success",
          cartItems: cartItemsWithGST.map((item) => ({
            productName: item.name,
            quantity: item.quantity,
            price: item.price,
            gstAmount: item.gstAmount,
            totalPriceWithGST: item.totalPriceWithGST,
          })),
        };
        try {
          // Send order data to the backend
          const backendResponse = await axios.post(
            "https://royalgreenhouse.co.in/backend/purchase.php",
            orderData
          );
          if (backendResponse.data.status === "success") {
            alert("Your order has been successfully placed!");
          } else {
            alert(
              "Error saving order to backend: " + backendResponse.data.message
            );
          }
        } catch (error) {
          console.error("Error saving order:", error);
          alert("Error saving order to backend: " + error.message);
        }
      },
      prefill: {
        name: address.fullName,
        contact: address.phoneNumber,
        email: address.email,
      },
      theme: {
        color: "#3399cc",
      },
      modal: {
        ondismiss: async function () {
          // Handle payment cancellation
          alert("Your payment has been successfully cancelled.");
          // Prepare order data with "Cancelled" status
          const cancelledOrderData = {
            fullName: address.fullName,
            phoneNumber: address.phoneNumber,
            areaColony: address.area,
            houseNumber: address.houseNumber,
            city: address.city,
            state: address.state,
            pincode: address.pincode,
            subtotal: subtotal,
            totalGST: totalGST,
            totalPrice: totalAmountWithGST,
            paymentMethod: "Razorpay",
            paymentStatus: "Cancelled",
            cartItems: cartItemsWithGST.map((item) => ({
              productName: item.name,
              quantity: item.quantity,
              price: item.price,
              gstAmount: item.gstAmount,
              totalPriceWithGST: item.totalPriceWithGST,
            })),
          };
          try {
            // Send cancelled order data to the backend
            const backendResponse = await axios.post(
              "https://royalgreenhouse.co.in/backend/purchase.php",
              cancelledOrderData
            );

            if (backendResponse.data.status === "success") {
              alert(
                "No order has been processed since the payment was cancelled."
              );
            } else {
              alert(
                "Error saving cancelled order to backend: " +
                  backendResponse.data.message
              );
            }
          } catch (error) {
            console.error("Error saving cancelled order:", error);
            alert("Error saving cancelled order to backend: " + error.message);
          }
        },
      },
    };

    const rzp = new window.Razorpay(options);
    rzp.open();
  };

  return (
    <div className="checkout-container">
      {step === 1 && (
        <div>
          <h2>Shipping Address</h2>
          <form onSubmit={handleNextStep}>
            {/* Full Name */}
            <div className="input-field">
              <label
                htmlFor="fullName"
                className={address.fullName ? "float-label" : ""}
              >
                Full Name
              </label>
              <input
                type="text"
                name="fullName"
                value={address.fullName}
                onChange={handleChange}
                required
              />
              {errors.fullName && <p className="error">{errors.fullName}</p>}
            </div>

            {/* Contact Number */}
            <div className="input-field">
              <label
                htmlFor="phoneNumber"
                className={address.phoneNumber ? "float-label" : ""}
              >
                Contact Number
              </label>
              <input
                type="text"
                name="phoneNumber"
                value={address.phoneNumber}
                onChange={handleChange}
                required
              />
              {errors.phoneNumber && (
                <p className="error">{errors.phoneNumber}</p>
              )}
            </div>

            {/* Email Address */}
            <div className="input-field">
              <label
                htmlFor="email"
                className={address.email ? "float-label-border" : ""}
              >
                Email Address
              </label>
              <input
                type="email"
                name="email"
                value={address.email}
                onChange={handleChange}
                required
              />
              {errors.email && <p className="error">{errors.email}</p>}
            </div>

            {/* House No./Building Name */}
            <div className="input-field">
              <label
                htmlFor="houseNumber"
                className={address.houseNumber ? "float-label" : ""}
              >
                House No./Building Name
              </label>
              <input
                type="text"
                name="houseNumber"
                value={address.houseNumber}
                onChange={handleChange}
                required
              />
              {errors.houseNumber && (
                <p className="error">{errors.houseNumber}</p>
              )}
            </div>

            {/* Road Name/Colony */}
            <div className="input-field">
              <label
                htmlFor="area"
                className={address.area ? "float-label" : ""}
              >
                Road Name/Colony
              </label>
              <input
                type="text"
                name="area"
                value={address.area}
                onChange={handleChange}
                required
              />
              {errors.area && <p className="error">{errors.area}</p>}
            </div>

            {/* City */}
            <div className="input-field">
              <label
                htmlFor="city"
                className={address.city ? "float-label" : ""}
              >
                City
              </label>
              <input
                type="text"
                name="city"
                value={address.city}
                onChange={handleChange}
                required
              />
              {errors.city && <p className="error">{errors.city}</p>}
            </div>

            {/* State */}
            <div className="input-field">
              <label
                htmlFor="state"
                className={address.state ? "float-label" : ""}
              >
                State
              </label>
              <input
                type="text"
                name="state"
                value={address.state}
                onChange={handleChange}
                required
              />
              {errors.state && <p className="error">{errors.state}</p>}
            </div>

            {/* Pincode */}
            <div className="input-field">
              <label
                htmlFor="pincode"
                className={address.pincode ? "float-label" : ""}
              >
                Pincode
              </label>
              <input
                type="text"
                name="pincode"
                value={address.pincode}
                onChange={handleChange}
                required
              />
              {errors.pincode && <p className="error">{errors.pincode}</p>}
            </div>

            <button className="full-width-button" type="submit">
              Next
            </button>
          </form>
        </div>
      )}

      {step === 2 && (
        <div>
          <h2>Order Summary</h2>
          <ul>
            {cartItemsWithGST.map((item) => (
              <li key={item.id} className="cart-item">
                <div className="order-summary-item">
                  <img
                    src={item.image}
                    alt={item.name}
                    className="order-summary-image"
                  />
                  <div className="order-summary-details">
                    <h4 className="product-name">{item.name}</h4>
                    <div className="quantity-controls">
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() =>
                          updateQuantity(item.id, item.quantity - 1)
                        }
                        disabled={item.quantity === 1}
                      >
                        -
                      </button>
                      <span>{item.quantity}</span>
                      <button
                        className="btn btn-outline-secondary btn-sm"
                        onClick={() =>
                          updateQuantity(item.id, item.quantity + 1)
                        }
                      >
                        +
                      </button>
                    </div>
                    {/* <div className="price-details">
                      <p>Price: ₹{item.price.toFixed(2)}</p>
                      <p>GST (18%): ₹{item.gstAmount.toFixed(2)}</p>
                      <p>Total: ₹{item.totalPriceWithGST.toFixed(2)}</p>
                    </div> */}
                  </div>
                </div>
              </li>
            ))}
          </ul>

          <div className="summary-totals">
            <p>Subtotal: ₹{subtotal.toFixed(2)}</p>
            <p>Total GST (18%): ₹{totalGST.toFixed(2)}</p>
            <h3>Total Amount: ₹{totalAmountWithGST.toFixed(2)}</h3>
          </div>
          <button className="full-width-button" onClick={handleNextStep}>
            Proceed to Payment
          </button>
        </div>
      )}

      {step === 3 && (
        <div>
          <h2>Payment</h2>
          <button className="full-width-button" onClick={handleRazorpayPayment}>
            Pay ₹{totalAmountWithGST.toFixed(2)}
          </button>
          {paymentSuccess && <p>Payment Successful!</p>}
        </div>
      )}
    </div>
  );
};

export default Checkout;
